import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Avatar,
  ButtonBase,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
  Badge,
  Chip,
  InputAdornment,
  Tooltip,
  alpha,
} from "@mui/material";
import { useAuth } from "../../AuthContext";
import { tokens } from "../../theme";
import { useTheme } from "@mui/material";
import { toast } from "react-toastify";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import MarkunreadOutlinedIcon from "@mui/icons-material/MarkunreadOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Divider from "@mui/material/Divider";
import SearchIcon from "@mui/icons-material/Search";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ChatPage from "./Chats";

import { buildUi } from "../global/uiTokens";
import { EmptyState, Loading, dialogPaperSx, fieldSx, primaryBtn } from "./userShared";

const STAFF_IMAGE_BASE = "https://mindsinaction.com.na/assets/media/pictures/";

/**
 * The same test the rest of the system uses: the column holds either the word
 * or a flag, depending on when the row was written.
 */
const isActiveStaff = (staff) =>
  ["active", "1"].includes(String(staff?.status || "").toLowerCase().trim());

const initials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

const Messages = ({ userId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const ui = useMemo(() => buildUi(colors, isDark), [colors, isDark]);
  const { getData, addData, updateData } = useAuth();
  // The search box used to be a bare <input> with nothing behind it: typing
  // in it did nothing at all. It filters the list now.
  const [search, setSearch] = useState("");
  const [messages, setMessages] = useState([]);
  const [chats, setChats] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [formData, setFormData] = useState({
    sender: userId,
    receiver: "",
    message: "",
    message_type: "text",
  });
  const [loading, setLoading] = useState(true);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [openStaffDialog, setOpenStaffDialog] = useState(false);

  useEffect(() => {
    async function fetchMessages() {
      try {
        const chatsResponse = await getData("proccess.php", "chat_V2");
        if (chatsResponse.success) {
          setChats(chatsResponse.data);
        } else {
          toast.error(chatsResponse.message || "Failed to load messages");
        }

        const staffsResponse = await getData("proccess.php", "staffs");
        if (staffsResponse.success) {
          setStaffs(staffsResponse.data);
        } else {
          toast.error(staffsResponse.message || "Failed to load messages");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch messages");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchMessages();
    const intervalId = setInterval(fetchMessages, 1000);
    return () => clearInterval(intervalId);
  }, [userId, getData]);

  const getUserNames = (userId) => {
    const staff = staffs.find((s) => String(s.Id) === String(userId));
    return staff ? `${staff.FirstName}` : "Unknown";
  };

  const setChat = async (chatID) => {
    try {
      setSelectedChatId(chatID);
      const response = await getData("proccess.php", "messages_V2", chatID);
      if (response.success) {
        setMessages(response.data);

        const messagesToUpdate = response.data.filter(
          (msg) => msg.status === 0
        );
        for (const message of messagesToUpdate) {
          if (message.status === 0 && message.receiver === userId) {
            try {
              const updatePayload = {
                convId: message.convId,
                sender: message.sender,
                receiver: message.receiver,
                message: message.message,
                status: 1,
              };
              const updateResponse = await updateData(
                "proccess.php",
                "messages_V2",
                updatePayload,
                message.id
              );
              if (!updateResponse.success) {
                console.error(
                  `Failed to update message ${message.id}: ${updateResponse.message}`
                );
              }
            } catch (err) {
              console.error(`Error updating message ${message.id}:`, err);
            }
          }
        }

        setFormData((prev) => ({
          ...prev,
          receiver:
            chats.find((c) => c.id === chatID).sender === userId
              ? chats.find((c) => c.id === chatID).receiver
              : chats.find((c) => c.id === chatID).sender,
        }));
      } else {
        const newChat = chats.find(
            (chat) =>
              (chat.id === chatID)
          );
          if (newChat && newChat.receiver !== userId) {
            setNewChat(newChat.id, newChat.receiver);
          }
      }
    } catch (err) {
      toast.error("Network error: Failed to load chat messages");
      console.error("Fetch error:", err);
    }
  };

  const setNewChat = (chatID, receiverId) => {
    setSelectedChatId(chatID);
    setMessages([]);
    setFormData((prev) => ({
      ...prev,
      receiver: receiverId,
    }));
  };

  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      message: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.receiver) {
      toast.error("Please select a chat to send a message");
      return;
    }
    if (!formData.message.trim()) {
      toast.error("Message cannot be empty");
      return;
    }

    try {
      const payload = {
        ...formData,
        convId: selectedChatId,
      };
      const response = await addData("proccess.php", "messages_V2", payload);
      if (response.success) {
        toast.success("Message sent successfully");
        setMessages((prev) => [...prev, payload]);
        setFormData((prev) => ({ ...prev, message: "" }));
        const chatsResponse = await getData("proccess.php", "chat_V2");
        if (chatsResponse.success) {
          setChats(chatsResponse.data);
        }
      } else {
        toast.error(response.message || "Failed to send message");
      }
    } catch (err) {
      toast.error("Network error: Failed to send message");
      console.error("Submit error:", err);
    }
  };

  const handleBackToChats = () => {
    setSelectedChatId(null);
    setMessages([]);
  };

  const handleOpenStaffDialog = () => {
    setOpenStaffDialog(true);
  };

  const handleCloseStaffDialog = () => {
    setOpenStaffDialog(false);
  };

  const handleStaffSelect = async (staffId) => {
    try {
      const chatPayload = {
        sender: userId,
        receiver: staffId,
        message: null,
        status: 0,
      };
      const response = await addData("proccess.php", "chat_V2", chatPayload);
      if (response.success) {
        toast.success("Chat created successfully");
        const chatsResponse = await getData("proccess.php", "chat_V2");
        if (chatsResponse.success) {
          setChats(chatsResponse.data);
          const newChat = chatsResponse.data.find(
            (chat) =>
              (chat.sender === userId && chat.receiver === staffId) ||
              (chat.sender === staffId && chat.receiver === userId)
          );
          if (newChat) {
            setNewChat(newChat.id, staffId);
          }
        }
        handleCloseStaffDialog();
      } else {
        toast.error(response.message || "Failed to create chat");
      }
    } catch (err) {
      toast.error("Network error: Failed to create chat");
      console.error("Create chat error:", err);
    }
  };

  /**
   * Your conversations, newest preview first, narrowed by the search box.
   *
   * This replaces two ~77-line branches - one for chats you sent, one for
   * chats you received - that were byte-for-byte identical. The only thing
   * that differed was the condition in front of them.
   */
  const myChats = useMemo(() => {
    const q = search.trim().toLowerCase();
    return chats
      .filter(
        (c) =>
          String(c.sender) === String(userId) ||
          String(c.receiver) === String(userId)
      )
      .filter((c) => {
        if (!q) return true;
        const otherId =
          String(c.sender) === String(userId) ? c.receiver : c.sender;
        const name = getUserNames(otherId).toLowerCase();
        return name.includes(q) || String(c.message || "").toLowerCase().includes(q);
      });
    // getUserNames reads staffs, so the list has to settle before the names do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats, staffs, userId, search]);

  /**
   * Who you can start a NEW conversation with: active colleagues you are not
   * already talking to. Someone who has left the organisation is deliberately
   * absent - there is nobody at the other end to read it.
   *
   * Existing conversations with them are still listed, flagged as former
   * staff, so the history is not lost.
   */
  const availableStaff = staffs.filter(
    (staff) =>
      String(staff.Id) !== String(userId) &&
      isActiveStaff(staff) &&
      !chats.some(
        (chat) =>
          (chat.sender === userId && chat.receiver === staff.Id) ||
          (chat.sender === staff.Id && chat.receiver === userId)
      )
  );

  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", md: "row" }}
      height="78vh"
      gap={1.5}
    >
      {/* ---------------- Conversations ---------------- */}
      <Box
        display={{ xs: selectedChatId ? "none" : "flex", md: "flex" }}
        flexDirection="column"
        p={1.5}
        width={{ xs: "100%", md: "330px" }}
        flexShrink={0}
        sx={{
          bgcolor: ui.surface,
          border: `1px solid ${ui.border}`,
          borderRadius: 3,
          boxShadow: ui.shadow,
          overflow: "hidden",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Typography variant="h5" fontWeight={800} color={ui.textPrimary}>
            Messages
          </Typography>
          <Tooltip title="Start a new conversation">
            <IconButton
              onClick={handleOpenStaffDialog}
              sx={{
                color: "#fff",
                background: ui.gradient,
                width: 34,
                height: 34,
                "&:hover": { background: ui.gradient, filter: "brightness(1.08)" },
              }}
            >
              <AddCommentOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <TextField
          size="small"
          fullWidth
          placeholder="Search a name or message…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={fieldSx(ui)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: ui.textSecondary }} />
              </InputAdornment>
            ),
          }}
        />
        <Box
          flex={1}
          marginTop="1rem"
          sx={{ overflowY: "auto", overflowX: "hidden" }}
        >
          {/* loading was set but never read, so the list just looked empty
              while the first fetch was in flight. */}
          {loading ? (
            <Loading ui={ui} label="Loading conversations…" />
          ) : myChats.length > 0 ? (
            myChats.map((chat) => {
              // The other person in the conversation, whichever side you are on.
              const otherId = String(chat.sender) === String(userId)
                ? chat.receiver
                : chat.sender;
              const other = staffs.find((x) => String(x.Id) === String(otherId));
              const name = getUserNames(otherId);
              const unread = Number(chat.status) === 0;
              const active = String(selectedChatId) === String(chat.id);
              // An inactive account means they have left the organisation.
              const departed = Boolean(other) && !isActiveStaff(other);

              return (
                <ButtonBase
                  key={chat.id}
                  onClick={() => setChat(chat.id)}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    textAlign: "left",
                    p: 1,
                    mb: 0.5,
                    borderRadius: 2,
                    bgcolor: active ? alpha(ui.accent, 0.12) : "transparent",
                    borderLeft: `3px solid ${active ? ui.accent : "transparent"}`,
                    "&:hover": { bgcolor: alpha(ui.accent, 0.08) },
                  }}
                >
                  <Badge
                    color="success"
                    variant="dot"
                    invisible={!unread}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  >
                    <Avatar
                      src={other?.image ? `${STAFF_IMAGE_BASE}${other.image}` : undefined}
                      sx={{
                        width: 40,
                        height: 40,
                        fontSize: 14,
                        fontWeight: 700,
                        filter: departed ? "grayscale(1)" : "none",
                        opacity: departed ? 0.75 : 1,
                      }}
                    >
                      {initials(name)}
                    </Avatar>
                  </Badge>

                  <Box flex={1} minWidth={0}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <Typography
                        noWrap
                        sx={{
                          fontWeight: 700,
                          fontSize: 14,
                          color: departed ? ui.textSecondary : ui.textPrimary,
                        }}
                      >
                        {name}
                      </Typography>
                      {departed && (
                        <Tooltip title="This person has left the organisation">
                          <Chip
                            size="small"
                            label="Former staff"
                            sx={{
                              height: 18,
                              fontSize: 10,
                              fontWeight: 700,
                              color: ui.textSecondary,
                              bgcolor: alpha(ui.textSecondary, 0.16),
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 12.5,
                        // An unread preview is worth noticing; a read one is not.
                        fontWeight: unread ? 700 : 400,
                        color: unread ? ui.accent : ui.textSecondary,
                      }}
                    >
                      {chat.message}
                    </Typography>
                  </Box>

                  {unread ? (
                    <MarkunreadOutlinedIcon sx={{ fontSize: 18, color: ui.accent }} />
                  ) : (
                    <DraftsOutlinedIcon
                      sx={{ fontSize: 18, color: alpha(ui.textSecondary, 0.8) }}
                    />
                  )}
                </ButtonBase>
              );
            })
          ) : (
            <EmptyState
              ui={ui}
              icon={<ForumOutlinedIcon />}
              title={search ? "Nothing matches" : "No conversations yet"}
              hint={
                search
                  ? "Try a different name."
                  : "Use the + button to start one."
              }
            />
          )}
        </Box>
      </Box>

      {/* ---------------- The conversation ---------------- */}
      <Box
        display={{ xs: selectedChatId ? "flex" : "none", md: "flex" }}
        position="relative"
        flexDirection="column"
        alignItems="center"
        p={1.5}
        flex={1}
        minWidth={{ xs: "100%", md: "350px" }}
        sx={{
          bgcolor: ui.surfaceAlt,
          border: `1px solid ${ui.border}`,
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        {selectedChatId ? (
          <>
            <Box
              display={{ xs: "block", md: "none", width: "100%" }}
              position="absolute"
              top="0"
              left="0"
            >
              <IconButton
                onClick={handleBackToChats}
                sx={{ p: 1, color: ui.textPrimary }}
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
              <Divider sx={{ borderColor: ui.border }} />
            </Box>

            <ChatPage userId={userId} params={messages} />
          </>
        ) : (
          <Box height="100%" display="grid" sx={{ placeItems: "center" }}>
            <EmptyState
              ui={ui}
              icon={<ForumOutlinedIcon />}
              title="No conversation open"
              hint="Pick someone on the left, or start a new one."
            />
          </Box>
        )}
        <Box
          component="form"
          onSubmit={handleSubmit}
          width="100%"
          mt={1.5}
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "flex-end",
            gap: 0.75,
            p: 1,
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
          }}
        >
          {/* Attaching is not wired up yet, so it says so rather than
              looking broken when nothing happens. */}
          <Tooltip title="Attachments are not available yet">
            <span>
              <IconButton disabled sx={{ color: ui.textSecondary }}>
                <AttachFileOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <TextField
            size="small"
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type your message…"
            value={formData.message}
            onChange={handleInputChange}
            sx={fieldSx(ui)}
          />

          <Button
            type="submit"
            disabled={!formData.message.trim() || !selectedChatId}
            sx={{ ...primaryBtn(ui), minWidth: 0, px: 1.75, py: 1 }}
          >
            <SendOutlinedIcon fontSize="small" />
          </Button>
        </Box>
      </Box>

      <Dialog
        open={openStaffDialog}
        onClose={handleCloseStaffDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx(ui) }}
      >
        <DialogTitle sx={{ color: ui.textPrimary, fontWeight: 700 }}>
          Start a conversation
        </DialogTitle>
        <Divider sx={{ borderColor: ui.border }} />
        <DialogContent sx={{ p: 0 }}>
          {availableStaff.length > 0 ? (
            <List disablePadding>
              {availableStaff.map((staff) => (
                /* ListItem button was deprecated in MUI v5 and removed in v6;
                   ListItemButton is the replacement. */
                <ListItemButton
                  key={staff.Id}
                  onClick={() => handleStaffSelect(staff.Id)}
                  sx={{
                    px: 3,
                    py: 1.25,
                    "&:hover": { bgcolor: alpha(ui.accent, 0.08) },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={staff.image ? `${STAFF_IMAGE_BASE}${staff.image}` : undefined}
                      sx={{ fontSize: 14, fontWeight: 700 }}
                    >
                      {initials(`${staff.FirstName} ${staff.LastName || ""}`)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography fontWeight={600} color={ui.textPrimary}>
                        {`${staff.FirstName || ""} ${staff.LastName || ""}`.trim()}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color={ui.textSecondary}>
                        {staff.department || staff.role || "Staff"}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <EmptyState
              ui={ui}
              icon={<ForumOutlinedIcon />}
              title="Nobody left to message"
              hint="You already have a conversation with everyone."
            />
          )}
        </DialogContent>
        <Divider sx={{ borderColor: ui.border }} />
        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button
            onClick={handleCloseStaffDialog}
            sx={{ textTransform: "none", color: ui.textSecondary }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Messages;